'use client';

import {
  createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState,
} from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useStreamStateV2 } from '@components/templates/bia/hooks/useStreamStateV2';
import { ChatAttachment } from '@components/features/candidate-prompt-bar/chatAttachment';
import { useChatPlaceholderKey, useChatSession, useResetChatOnSignOut } from '@hooks/features/useChatSession';

interface BestieChatStateValue {
  /** Bestie chat stream — shared so it survives across the views that render the chat. */
  stream: ReturnType<typeof useStreamStateV2>['stream'];
  /** Submit a chat message to the Bestie stream, optionally with one staged file. */
  submitChat: (text: string, attachment?: ChatAttachment | null) => Promise<void>;
  /** Loco key for the chat input's placeholder, chosen by whether the visitor is an employer. */
  inputPlaceholderKey: string;
  /** Flag that the next chat view should open its input ready to type (e.g. when the
   *  user picks "coaching" from the prompt-bar menu). Lives here because ChatBranch
   *  remounts across the search↔chat mode switch, so a window event would race. */
  requestChatFocus: () => void;
  /** Read-and-clear the chat-focus request. Returns true once per request. */
  consumeChatFocusRequest: () => boolean;
}

const BestieChatStateContext = createContext<BestieChatStateValue | undefined>(undefined);

/**
 * @description Holds the shared live state for the Bestie candidate chat — the chat stream and
 * its submit handler. Mounted by the Bestie chat page so the stream + submit are available to
 * the prompt bar and the streamed-messages view.
 */
export const BestieChatStateProvider = ({ children, locale }: {
  children: ReactNode;
  locale: Locale;
}) => {
  // Signing out must not leave the previous user's conversation on screen. Wipes stored sessions and
  // reloads — see the hook for why a reload rather than a state reset, and which sign-out path made
  // this necessary.
  useResetChatOnSignOut();

  const inputPlaceholderKey = useChatPlaceholderKey();
  const { threadId, isResumed, touchSession } = useChatSession();
  const { stream, startStream, initializeWithHistory } = useStreamStateV2(null, { threadId });

  const streamHeaders = useMemo(() => ({
    'x-anon-session-id': threadId ?? '',
  }), [threadId]);

  const submitChat = useCallback(async (text: string, attachment?: ChatAttachment | null) => {
    // Drop the send until the session has resolved. `useStreamStateV2` falls back to
    // `useAnonSessionId()` when given a null `threadId`, which for a signed-in user is the legacy
    // permanent `default:<userId>` thread — so sending in this window would write into the thread
    // rolling sessions replace. The window is a single render (resolution is a `localStorage` read,
    // no network), and the prompt bar is disabled while the opener runs, so this is a guard rather
    // than a user-visible restriction.
    if (!threadId) return;

    // Text may be empty when the user sends a bare file, and that needs no substitute caption:
    // the agents service prepends the archived-file markdown link to the turn, so the model still
    // sees the file and its URL, and the skill that ingests a CV triggers on the attachment itself
    // rather than on an instruction. The bubble renders the chip alone, live and on reload alike.
    const message = {
      id: uuidv4(),
      content: text,
      type: 'human' as const,
      ...(attachment && { attachmentName: attachment.filename }),
    };
    // Slide the 3h window on the user's half of the turn.
    touchSession();
    await startStream(message, '', {
      headers: streamHeaders,
      configurations: { user_locale: locale || 'ro' },
      attachment,
    });
  }, [threadId, startStream, streamHeaders, locale, touchSession]);

  // ...and on the agent's half. Both parties keep the session alive, so a conversation that is
  // being read rather than typed into does not expire underneath the reader.
  //
  // Keyed on the message id so a streaming reply, which mutates one message in place as tokens
  // arrive, writes once when it first appears rather than on every chunk.
  //
  // Restored history is excluded: its ids are synthetic (`history-N`, see `mapChatMessagesToMessages`)
  // and it is a replay of turns that already slid the window when they happened. Counting it would
  // make the session immortal for anyone who reloads the page inside the TTL, which is the opposite
  // of a window that measures conversation.
  const touchedForMessageIdRef = useRef<string | null>(null);
  useEffect(() => {
    const messages = stream?.messages;
    const last = messages?.[messages.length - 1];
    if (last?.type !== 'ai' || !last.id) return;
    if (last.id.startsWith('history-')) return;
    if (touchedForMessageIdRef.current === last.id) return;

    touchedForMessageIdRef.current = last.id;
    touchSession();
  }, [stream?.messages, touchSession]);

  const [isHistoryLoading, setIsHistoryLoading] = useState(false);
  const [hasHistoryLoaded, setHasHistoryLoaded] = useState(false);

  // The thread id can change under a mounted provider — the visitor signs in through a modal
  // without leaving the page. `hasHistoryLoaded` was only ever set for the old thread, so reset it
  // and let the button come back for the new one, whose history was never loaded.
  useEffect(() => {
    setHasHistoryLoaded(false);
  }, [threadId]);

  const loadHistory = useCallback(async () => {
    if (isHistoryLoading || hasHistoryLoaded) return;
    setIsHistoryLoading(true);
    try {
      const history = await initializeWithHistory();
      // `fetched === false` means the fetch was skipped because there was no thread id yet —
      // nothing was restored, so leave the button in place rather than marking this done.
      if (history.fetched !== false) setHasHistoryLoaded(true);
    } catch {
      // Deliberately silent, no toast: the button stays visible so the visitor can retry.
    } finally {
      setIsHistoryLoading(false);
    }
  }, [initializeWithHistory, isHistoryLoading, hasHistoryLoaded]);

  // A resumed session restores its transcript automatically — a plain read, no decision. The session
  // was already decided synchronously from the stored expiry, so there is nothing to undo here and
  // no verdict to wait for. A thread whose checkpoint does not exist yet simply comes back empty on a
  // valid id, which the next message starts.
  useEffect(() => {
    if (!isResumed || hasHistoryLoaded || isHistoryLoading) return;
    void loadHistory();
  }, [isResumed, hasHistoryLoaded, isHistoryLoading, loadHistory]);

  // **Nobody gets an automatic opener** — signed-in visitors and guests alike land on the canned
  // `CoachingLanding` greeting (one random entry from `useCoachingGreeting`'s pool) and the chat
  // starts on their first message.
  //
  // Signed-in visitors used to be greeted by the agent's `home-greeting` skill via `chat/invoke`,
  // seeded into the transcript with its job payload; guests, before that, by `BESTIE_GREETING_PROMPT`
  // posted as a hidden `human` turn. Both wrote a turn into the thread on every page entry — the
  // greeting itself persisted the rolling session — and both replaced the landing with a chat view
  // the visitor had not asked for. `isBestieGreetingPrompt` / `stripGreetingExchange` stay in place:
  // threads written before these changes still carry those turns and must keep hiding them.

  // One-shot intent so a chat-view mount can auto-open its input. A ref (not state)
  // because it's consumed by an effect on the next mount, not rendered.
  const chatFocusRequestRef = useRef(false);
  const requestChatFocus = useCallback(() => { chatFocusRequestRef.current = true; }, []);
  const consumeChatFocusRequest = useCallback(() => {
    const requested = chatFocusRequestRef.current;
    chatFocusRequestRef.current = false;
    return requested;
  }, []);

  const value = useMemo(
    () => ({
      stream,
      submitChat,
      inputPlaceholderKey,
      requestChatFocus,
      consumeChatFocusRequest,
    }),
    [
      stream, submitChat, inputPlaceholderKey, requestChatFocus, consumeChatFocusRequest,
    ],
  );

  return <BestieChatStateContext.Provider value={value}>{children}</BestieChatStateContext.Provider>;
};

/** Access the MC shared chat stream + submit handler. Returns undefined outside the candidate shell. */
export const useBestieChatStateOptional = () => useContext(BestieChatStateContext);

export const useBestieChatState = () => {
  const ctx = useContext(BestieChatStateContext);
  if (!ctx) throw new Error('useBestieChatState must be used within BestieChatStateProvider');
  return ctx;
};
